import h from 'hyperscript';

import playStateIcon from '@disy/cadenza-icons/play-state.svg';
import { requireNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { icon } from '@disy/ui/icon';

import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import { isTimelinePlayerMapView } from 'cadenza/workbook/timelines/timeline.state';
import type { View } from 'cadenza/workbook/view/view';
import type { UpdateableElement } from 'cadenza/workbook/view/view-filters-button/view-filters-button';
import { createViewFilterIndicatorButtonIcon } from 'cadenza/workbook/view/view-filters-button/view-filters-button';
import { type IViewToolbar } from 'cadenza/workbook/view/view-header/i-view-toolbar';
import { ViewActionsToolbar } from 'cadenza/workbook/view/view-header/view-actions-toolbar';
import { createViewInfoButton } from 'cadenza/workbook/view/view-header/view-actions-toolbar-button-provider';
import {
  isTitleBarVisible,
  isViewActionToolbarShown,
  isViewSmall,
  titlebarVisibilitySetting,
} from 'cadenza/workbook/view/view-header/view-toolbar-utils';
import 'cadenza/workbook/view/view-header/worksheet-view-toolbar.css';
import { getMapView } from 'cadenza/workbook/view/view-util';
import {
  getActiveViewId,
  getNotificationsForView,
  isActiveView,
  isDetailView,
  isIntegratedView,
  isViewLoading,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { isLinearLayout, worksheetLayout } from 'cadenza/workbook/worksheet-layout';
import { getDraggableOrNonDraggableClass } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';

const COMPONENT_NAME = 'd-worksheet-view-toolbar';
const VIEW_COMPONENT_NAME = 'd-worksheet-view';

export class WorksheetViewToolbar extends HTMLElement implements IViewToolbar {
  readonly #view;
  readonly #resizeObserver;
  #viewActionsToolbar?: ViewActionsToolbar;

  #isViewSmall = false;
  #tearDowns: Teardown[] = [];

  #title?: HTMLElement;
  #titleBar?: HTMLElement;
  #timelinePlayerIcon?: HTMLElement;
  #filterIndicatorButtonIcon?: UpdateableElement;
  #viewInfoButton?: UpdateableElement;

  constructor(view: View) {
    super();
    this.#view = view;
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (isIntegratedView(getStore().getState(), this.#view.viewId)) {
        return;
      }
      const targetView = entry.target as View;
      if (isViewSmall(targetView) !== this.#isViewSmall) {
        this.update();
      }
    });
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.#timelinePlayerIcon = h('span', icon(playStateIcon));
    this.#timelinePlayerIcon.hidden = true;
    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-view-analysis-context')) {
      this.#filterIndicatorButtonIcon = createViewFilterIndicatorButtonIcon();
    }
    this.#viewInfoButton = createViewInfoButton(this.#view.viewId, `${COMPONENT_NAME}--info-button`);
    this.#titleBar = this.#createTitleBar();
    this.append(this.titleBar);
    if (this.viewActionsToolbar) {
      this.append(this.viewActionsToolbar);
    }

    this.#tearDowns = [
      // update the toolbar when the view finished loading
      observeStore(
        getStore(),
        (state) => isViewLoading(state, this.#view.viewId),
        (isLoading) => this.#updateMapToolbarVisibility(isLoading),
      ),
      // ensure that the view toolbar matches activation state
      observeStore(
        getStore(),
        (state) => isActiveView(state, this.#view.viewId),
        () => this.#updateMapToolbarVisibility(),
      ),
      // let other store updates finish first
      observeStore(
        getStore(),
        (state) => getNotificationsForView(state, this.#view.viewId),
        (notifications) => this.#onNotificationsChange(notifications),
      ),
      observeStore(getStore(), (state) => isDetailView(state, this.#view.viewId), this.update.bind(this)),
      on(worksheetLayout, 'change', () => this.update()),
    ];
    this.#resizeObserver.observe(this.#view);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#tearDowns = tearDown(this.#tearDowns);
  }

  /**
   * MapViews have an additional toolbar that must be taken care of.
   *
   * @param isMapViewLoading - If true then do nothing because the view is not yet ready.
   */
  #updateMapToolbarVisibility(isMapViewLoading = false) {
    if (isMapViewLoading) {
      return; // cannot interact
    }
    // Map view can be null only during the initialization phase
    if (this.#view.type === ViewType.MAP && getMapView(this.#view) != null) {
      const workbookMapView = getMapView(this.#view);
      if (this.#view.viewId === getActiveViewId(getStore().getState())) {
        workbookMapView.showToolbar();
      } else {
        workbookMapView.hideToolbar();
      }
    }
  }

  update() {
    // set isViewSmall-state first to prevent duplicate resize actions
    this.#isViewSmall = isViewSmall(this.#view);
    this.#updateMapToolbarVisibility();
    const titleBarVisibilityFromSettings = titlebarVisibilitySetting(this.#view);
    const titleBarVisible = isTitleBarVisible(this.#view, this.#isViewSmall);
    if (
      isLinearLayout() &&
      (this.#view.type === 'CONTROLLER' || this.#view.type === 'TEXT') &&
      titleBarVisibilityFromSettings === 'auto'
    ) {
      this.titleBar.classList.remove('visuallyhidden');
    } else {
      this.titleBar.classList.toggle('visuallyhidden', !titleBarVisible);
    }

    if (titleBarVisible) {
      const draggableClassToToggle = getDraggableOrNonDraggableClass();
      this.titleBar.classList.toggle(draggableClassToToggle, titleBarVisible);
      this.titleBar.classList.toggle('is-minified', !titleBarVisible);
      requireNonNullable(this.#title).hidden =
        titleBarVisibilityFromSettings === 'off' && isDetailView(getStore().getState(), this.#view.viewId);
    }
    this.classList.toggle('is-minified', !titleBarVisible);

    this.viewActionsToolbar?.update();
    this.#updateTimelinePlayerIcon();
    this.#updateTitleText();
    this.#filterIndicatorButtonIcon?.update?.(this.#view);
    this.#viewInfoButton?.update?.(this.#view);
  }

  #createTitleBar() {
    this.#title = h(`h3.${VIEW_COMPONENT_NAME}--title.ellipsis.${getDraggableOrNonDraggableClass()}`, this.#view.name);
    return h(`.${VIEW_COMPONENT_NAME}--titlebar`, [
      this.#timelinePlayerIcon,
      this.#title,
      this.#filterIndicatorButtonIcon,
      this.#viewInfoButton,
    ]);
  }

  #updateTimelinePlayerIcon() {
    const isTimelinePlayerMap = isTimelinePlayerMapView(getStore().getState(), this.#view.viewId);
    if (this.#timelinePlayerIcon) {
      this.#timelinePlayerIcon.hidden = !isTimelinePlayerMap;
    }
  }

  #updateTitleText() {
    const titleElement = this.querySelector(`.${VIEW_COMPONENT_NAME}--title`);
    if (titleElement) {
      titleElement.textContent = this.#view.name;
    }
  }

  get viewActionsToolbar() {
    if (!this.#viewActionsToolbar) {
      this.#viewActionsToolbar = isViewActionToolbarShown() ? new ViewActionsToolbar(this.#view) : undefined;
    }
    return this.#viewActionsToolbar;
  }

  #onNotificationsChange(notifications: WorkbookNotification[]) {
    const warningCount = notifications.filter((notification) => notification.level === 'WARNING').length;
    const errorCount = notifications.filter((notification) => notification.level === 'ERROR').length;
    const infoCount = notifications.filter((notification) => notification.level === 'INFO').length;
    const hasRelevantNotifications = warningCount > 0 || errorCount > 0;

    this.classList.toggle('has-notifications', hasRelevantNotifications);
    this.classList.toggle('has-one-info', errorCount === 0 && warningCount === 0 && infoCount === 1);
    this.classList.toggle('has-one-warning', errorCount === 0 && warningCount === 1 && infoCount === 0);
    this.classList.toggle('has-one-error', errorCount === 1 && warningCount === 0 && infoCount === 0);
  }

  get titleBar() {
    return requireNonNullable(this.#titleBar);
  }
}

customElements.define(COMPONENT_NAME, WorksheetViewToolbar);
